module Meevo.Components.GlobalNav {

    export class MeevoGlobalNavCommandRow implements ng.IDirective {
        restrict = "E";
        templateUrl = "app/components/globalNav/globalNavCommandRow.template.html";
        scope = false;

        link(scope: ng.IScope, element: JQuery): void {
            // meevo-global-nav lives behind two ng-if gates (isAuthenticated +
            // enhancedGlobalNavEnabled), so this element is absent from the DOM during
            // Pendo's initial scan. Once the template is compiled:
            //   - loadGuides()        → re-activates click-triggered shortcut guides
            //   - getResourceCenter() → forces the resource center to initialise and
            //                           re-scan for its configured launcher element,
            //                           injecting pendo-resource-center-badge-notification-bubble
            // pendo.loadGuides is NOT in the stub queue (unlike initialize/pageLoad), so
            // its presence is a reliable signal that pendo.js has fully loaded.
            // Poll up to 30 s to handle Pendo loading after the template renders.
            const pendoReady = () => {
                const pendo = (window as any).pendo;
                return pendo && typeof pendo.loadGuides === 'function';
            };

            const notifyPendo = () => {
                const pendo = (window as any).pendo;
                pendo.loadGuides();
                try {
                    if (pendo.BuildingBlocks &&
                        pendo.BuildingBlocks.BuildingBlockResourceCenter &&
                        typeof pendo.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter === 'function') {
                        pendo.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter();
                    }
                } catch (_) {}
            };

            // Pendo positions its "i" shortcut badge with page coordinates computed from
            // the convobar input's rect (offsets recorded against the legacy 51px bar), so
            // in the enhanced bar it lands on the input's right edge instead of the old
            // info-icon position. Reparent it into the reserved slot after the clear
            // button — DOM event listeners travel with the element, so Pendo's click
            // still opens the guide, and the slot's LESS pins it into normal flex flow.
            //
            // To avoid the badge flashing at Pendo's coordinates before the move, a
            // MutationObserver hides each newly-inserted badge in the same tick (before
            // it can paint) and reveals it only once it is either placed in the slot or
            // classified as belonging to another guide elsewhere on the page. A slow
            // interval sweep remains as a backstop.
            const root = element[0];
            const getSlot = () => root.querySelector('.enhanced-convobar__pendo-badge-slot');

            // 'ours' → belongs to this convobar; 'other' → some other guide's badge;
            // 'unknown' → Pendo hasn't positioned it yet, decide again shortly.
            const classifyBadge = (badge: HTMLElement): string => {
                if (root.contains(badge)) return 'ours';
                const convo = root.querySelector('.convobar-container.enhanced-convobar');
                if (!convo) return 'other';
                const convoRect = convo.getBoundingClientRect();
                const rect = badge.getBoundingClientRect();
                if (!convoRect.width || (!rect.width && !rect.height)) return 'unknown';
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                return (centerX >= convoRect.left && centerX <= convoRect.right + 60 &&
                        centerY >= convoRect.top - 20 && centerY <= convoRect.bottom + 20)
                    ? 'ours' : 'other';
            };

            const settleBadge = (badge: HTMLElement) => {
                badge.style.visibility = 'hidden';
                let tries = 0;
                const decide = () => {
                    // Never leave a badge invisible: every exit path restores visibility.
                    if (!document.body.contains(badge)) return;
                    const slot = getSlot();
                    if (slot && slot.contains(badge)) {
                        badge.style.visibility = '';
                        return;
                    }
                    const verdict = classifyBadge(badge);
                    if (verdict === 'unknown' && ++tries < 20) {
                        setTimeout(decide, 50);
                        return;
                    }
                    if (verdict === 'ours' && slot) {
                        slot.appendChild(badge);
                    }
                    badge.style.visibility = '';
                };
                setTimeout(decide, 0);
            };

            const sweepBadges = () => {
                const slot = getSlot();
                const badges = document.querySelectorAll('._pendo-badge');
                for (let i = 0; i < badges.length; i++) {
                    const badge = badges[i] as HTMLElement;
                    if (slot && slot.contains(badge)) continue;
                    if (classifyBadge(badge) === 'ours' && slot) {
                        slot.appendChild(badge);
                    }
                }
            };

            const MutationObserverCtor = (window as any).MutationObserver;
            let badgeObserver: any = null;
            if (MutationObserverCtor) {
                badgeObserver = new MutationObserverCtor((mutations: any[]) => {
                    for (let m = 0; m < mutations.length; m++) {
                        const added = mutations[m].addedNodes;
                        for (let i = 0; i < added.length; i++) {
                            const node = added[i];
                            if (!node || node.nodeType !== 1) continue;
                            if (node.classList && node.classList.contains('_pendo-badge')) {
                                settleBadge(node);
                            } else if (node.querySelector) {
                                const badge = node.querySelector('._pendo-badge');
                                if (badge) settleBadge(badge as HTMLElement);
                            }
                        }
                    }
                });
                badgeObserver.observe(document.body, { childList: true, subtree: true });
            }

            sweepBadges();
            const badgeWatcher = setInterval(sweepBadges, 2000);

            let poll: any = null;
            if (pendoReady()) {
                notifyPendo();
            } else {
                let attempts = 0;
                poll = setInterval(() => {
                    if (pendoReady()) {
                        notifyPendo();
                        clearInterval(poll);
                    } else if (++attempts >= 60) {
                        clearInterval(poll);
                    }
                }, 500);
            }

            scope.$on('$destroy', () => {
                if (poll) { clearInterval(poll); }
                clearInterval(badgeWatcher);
                if (badgeObserver) { badgeObserver.disconnect(); }
            });
        }

        static factory(): angular.IDirectiveFactory {
            const directive = () => {
                return new MeevoGlobalNavCommandRow();
            };

            return directive;
        }
    }

    angular.module("meevo.components.globalNav")
        .directive("meevoGlobalNavCommandRow", MeevoGlobalNavCommandRow.factory());
}